import React, { useState, useCallback } from 'react';
import type { PlaybookApproval, ApprovalDecision } from '../types/playbook.types';

interface ApprovalStepperProps {
  approvals: PlaybookApproval[];
  currentUserId: string;
  onApprove: (decision: ApprovalDecision, comment?: string) => void;
  loading?: boolean;
}

const STEP_COLORS: Record<string, { dot: string; ring: string; text: string }> = {
  approved: { dot: 'bg-emerald-500', ring: 'ring-emerald-200 dark:ring-emerald-800', text: 'text-emerald-700 dark:text-emerald-300' },
  pending: { dot: 'bg-gray-300 dark:bg-gray-600', ring: 'ring-gray-200 dark:ring-gray-700', text: 'text-gray-500 dark:text-gray-400' },
  rejected: { dot: 'bg-rose-500', ring: 'ring-rose-200 dark:ring-rose-800', text: 'text-rose-700 dark:text-rose-300' },
  skipped: { dot: 'bg-gray-400', ring: 'ring-gray-200 dark:ring-gray-700', text: 'text-gray-400 dark:text-gray-500' },
  current: { dot: 'bg-lex-accent', ring: 'ring-lex-accent', text: 'text-lex-accent' },
};

const ApprovalStepper: React.FC<ApprovalStepperProps> = ({ approvals, currentUserId, onApprove, loading }) => {
  const [rejectComment, setRejectComment] = useState('');
  const [showReject, setShowReject] = useState(false);

  const sorted = [...approvals].sort((a, b) => a.step_order - b.step_order);
  const currentStepIndex = sorted.findIndex((s) => s.status === 'pending');

  const handleApprove = useCallback(() => {
    onApprove('approved');
  }, [onApprove]);

  const handleReject = useCallback(() => {
    if (!rejectComment.trim()) return;
    onApprove('rejected', rejectComment.trim());
    setRejectComment('');
    setShowReject(false);
  }, [onApprove, rejectComment]);

  const isCurrentUserStep = (step: PlaybookApproval, index: number): boolean => {
    if (index !== currentStepIndex) return false;
    if (step.assigned_user_id) return step.assigned_user_id === currentUserId;
    return true; // Role-based — allow if user has the role (backend validates)
  };

  return (
    <div className="space-y-4">
      {/* Stepper */}
      <div className="flex items-center gap-1">
        {sorted.map((step, i) => {
          const isCurrent = i === currentStepIndex;
          const colorKey = isCurrent ? 'current' : step.status;
          const colors = STEP_COLORS[colorKey] || STEP_COLORS.pending;

          return (
            <React.Fragment key={step.id}>
              <div className="flex flex-col items-center gap-1 min-w-0">
                <div className={`relative flex h-8 w-8 items-center justify-center rounded-full ring-2 ${colors.ring}`}>
                  <span
                    className={`h-3 w-3 rounded-full ${colors.dot}`}
                    style={isCurrent ? { animation: 'pulseOnce 1.5s ease-in-out infinite' } : undefined}
                  />
                  {step.status === 'approved' && (
                    <svg className="absolute h-4 w-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {step.status === 'rejected' && (
                    <svg className="absolute h-4 w-4 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
                <span className={`text-[10px] font-medium truncate max-w-[80px] text-center ${colors.text}`}>
                  {step.required_role}
                </span>
                {step.decisionByUser && step.status !== 'pending' && (
                  <span className="text-[9px] text-gray-400 dark:text-gray-500 truncate max-w-[80px]">
                    {step.decisionByUser.first_name} {step.decisionByUser.last_name}
                  </span>
                )}
              </div>
              {i < sorted.length - 1 && (
                <div className={`flex-1 h-0.5 min-w-[16px] ${
                  i < currentStepIndex ? 'bg-emerald-300 dark:bg-emerald-700' : 'bg-gray-200 dark:bg-gray-700'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Action buttons for current step user */}
      {currentStepIndex >= 0 && isCurrentUserStep(sorted[currentStepIndex], currentStepIndex) && (
        <div className="space-y-3">
          {showReject ? (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Rejection reason <span className="text-rose-500">*</span>
              </label>
              <textarea
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-500"
                rows={2}
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Explain why this is being rejected..."
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={!rejectComment.trim() || loading}
                  className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Confirm Rejection
                </button>
                <button
                  type="button"
                  onClick={() => setShowReject(false)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Back
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleApprove}
                disabled={loading}
                className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? 'Approving...' : 'Approve'}
              </button>
              <button
                type="button"
                onClick={() => setShowReject(true)}
                disabled={loading}
                className="rounded-md border border-rose-300 px-3 py-1.5 text-sm font-medium text-rose-700 hover:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:bg-rose-900/30 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Reject
              </button>
            </div>
          )}
        </div>
      )}

      {/* Non-current users see read-only message */}
      {currentStepIndex >= 0 && !isCurrentUserStep(sorted[currentStepIndex], currentStepIndex) && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Awaiting <strong>{sorted[currentStepIndex].required_role}</strong> approval
          {sorted[currentStepIndex].assignedUser && (
            <> from {sorted[currentStepIndex].assignedUser!.first_name} {sorted[currentStepIndex].assignedUser!.last_name}</>
          )}
        </p>
      )}

      {/* Rejection banner */}
      {sorted.some((s) => s.status === 'rejected') && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 dark:border-rose-800 dark:bg-rose-900/20">
          {sorted
            .filter((s) => s.status === 'rejected')
            .map((s) => (
              <div key={s.id} className="text-sm text-rose-700 dark:text-rose-200">
                <span className="font-medium">Rejected by {s.decisionByUser?.first_name || s.required_role}:</span>
                {s.decision_comment && <span className="ml-1">{s.decision_comment}</span>}
              </div>
            ))}
        </div>
      )}
    </div>
  );
};

export default ApprovalStepper;
